import React, { useState } from 'react';
import { Table, Card, Empty, Typography, Tag } from 'antd';
import { useAuth } from '../../hooks/useAuth';
import { useGetSurveyFinalResponsesQuery } from '../../services/nestjs-base-api';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Text } = Typography;

interface OpenResponsesProps {
    survey: any;
    loading?: boolean;
}

const OpenResponses: React.FC<OpenResponsesProps> = ({ survey, loading }) => {
    const { user } = useAuth();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const { data: responsesData, isLoading, refetch } = useGetSurveyFinalResponsesQuery({
        jwtToken: user!.jwtToken,
        orgId: user!.organisation.id,
        surveyId: survey.id,
        page: currentPage,
        limit: pageSize,
    }, {
        skip: !survey.id
    });

    const responses = responsesData?.data || [];
    const total = responsesData?.total || 0;

    const columns: ColumnsType<any> = [
        {
            title: 'Participant',
            dataIndex: 'participantIdentifier',
            key: 'participantIdentifier',
            width: 200,
            render: (identifier: string) => (
                <Text code>{identifier}</Text>
            ),
        },
        {
            title: 'Email',
            dataIndex: 'participantEmail',
            key: 'participantEmail',
            width: 200,
            render: (email?: string) => (
                email ? <Text>{email}</Text> : <Text type="secondary">-</Text>
            ),
        },
        {
            title: 'Question',
            dataIndex: 'questionText',
            key: 'questionText',
            width: 250,
            ellipsis: true,
            render: (text: string) => (
                <Text strong>{text}</Text>
            ),
        },
        {
            title: 'Response',
            dataIndex: 'responseText',
            key: 'responseText',
            ellipsis: true,
            render: (text: string) => (
                text ? (
                    <Text>{text}</Text>
                ) : (
                    <Tag color="default">Skipped</Tag>
                )
            ),
        },
        {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 180,
            render: (date: string) => (
                <Text type="secondary">
                    {dayjs(date).format('MMM D, YYYY h:mm A')}
                </Text>
            ),
        },
    ];

    // Check if survey has a final question configured
    const hasFinalQuestion = survey?.settings?.finalQuestionId;

    if (!loading && (!responses || responses.length === 0)) {
        return (
            <Card>
                <Empty
                    description={
                        hasFinalQuestion ? (
                            <div>
                                <Text strong>No Open Responses Yet</Text>
                                <br />
                                <Text type="secondary">
                                    Open-ended responses from the final question will appear here once respondents complete the survey.
                                </Text>
                            </div>
                        ) : (
                            <div>
                                <Text strong>No Final Question Configured</Text>
                                <br />
                                <Text type="secondary">
                                    This survey does not have a final question configured. Edit the survey to add an optional open-ended question at the end.
                                </Text>
                            </div>
                        )
                    }
                />
            </Card>
        );
    }

    return (
        <Card>
            <Table
                columns={columns}
                dataSource={responses}
                loading={isLoading || loading}
                rowKey="id"
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: total,
                    onChange: (page, size) => {
                        setCurrentPage(page);
                        setPageSize(size || 20);
                    },
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} responses`,
                    pageSizeOptions: ['10', '20', '50', '100'],
                }}
            />
        </Card>
    );
};

export default OpenResponses;
